import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, Modal } from 'react-bootstrap';
import { getNominationRoundYear } from '../../../../utils/getNominationRoundYear';

const ROLE_LABELS = {
  'committee-chair': 'Committee Chair',
  'council-member': 'Council Member',
  'committee-member': 'Committee Member',
  admin: 'Admin',
  'super-admin': 'Admin',
};

const CoiHistoryModal = ({ show, onHide, coiArchives, users, coiTypes }) => {
  const groupedByYear = coiArchives.reduce((acc, declaration) => {
    const archivedAt = declaration.attributes?.archivedAt;
    const year = archivedAt
      ? getNominationRoundYear(new Date(archivedAt))
      : 'Unknown';

    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(declaration);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedByYear).sort((a, b) => b - a);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>COI Declaration History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Accordion defaultActiveKey={sortedYears[0]}>
          {sortedYears.map((year) => (
            <Accordion.Item eventKey={year} key={year}>
              <Accordion.Header>
                <span className="fw-bold me-2">Round {year}</span>
                <span className="badge bg-secondary rounded-pill">
                  {groupedByYear[year].length}
                </span>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                {[...groupedByYear[year]]
                  .sort((a, b) => {
                    const nameA = (users[a.attributes?.userId] || '').replace(
                      /\s*\(.*\)$/,
                      '',
                    );
                    const nameB = (users[b.attributes?.userId] || '').replace(
                      /\s*\(.*\)$/,
                      '',
                    );
                    return nameA.localeCompare(nameB);
                  })
                  .map((declaration, index) => {
                    const attrs = declaration.attributes;
                    const fullName = users[attrs.userId] || 'Unknown User';
                    const userName = fullName.replace(/\s*\(.*\)$/, '');
                    const roleLabel = ROLE_LABELS[attrs.userRole] || '';
                    const coiTypeNames = (attrs.coiTypes?.data || [])
                      .map(
                        (ct) =>
                          coiTypes[ct.id] || ct.attributes?.name || 'Unknown',
                      )
                      .filter(Boolean);

                    return (
                      <div
                        key={declaration.id}
                        className={`card ${index < groupedByYear[year].length - 1 ? 'mb-3' : ''}`}
                      >
                        <div className="card-body py-3 px-3">
                          <div className="d-flex align-items-center mb-2">
                            <i className="bi bi-person me-2" />
                            <span className="fw-bold">{userName}</span>
                            {roleLabel && (
                              <span className="text-muted ms-2">
                                · {roleLabel}
                              </span>
                            )}
                          </div>

                          {coiTypeNames.length > 0 && (
                            <div className="d-flex align-items-start mb-2">
                              <i className="bi bi-tag me-2 mt-1" />
                              <div>
                                {coiTypeNames.map((typeName) => (
                                  <span
                                    key={typeName}
                                    className="badge border border-primary text-primary me-1 mb-1"
                                  >
                                    {typeName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {attrs.coiDetail && (
                            <div className="d-flex align-items-start">
                              <i className="bi bi-chat-left-text me-2 mt-1" />
                              <span>{attrs.coiDetail}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" type="button" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

CoiHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  coiArchives: PropTypes.array.isRequired,
  users: PropTypes.object.isRequired,
  coiTypes: PropTypes.object.isRequired,
};

export default CoiHistoryModal;
